import { uniqueArray } from "@msuite/utils/array";
import type { Action } from "../actions";

interface SubjectConfig {
	// readonly, because the map below is `as const` — that is what preserves
	// each subject's action literals so ActionFor can narrow per subject.
	actions: readonly Action[];
}

// The subject/action manifest — the single source of truth for which subjects
// exist and which actions they support. AppSubjectName in types.ts is derived
// from these keys, and permissionManifest below drives the permission grid,
// so adding a subject here is the only edit needed to make it valid
// everywhere. Deliberately typed via `satisfies` rather than an explicit
// annotation: `Record<string, ...>` would widen `keyof typeof` to `string`
// and silently disable the derivation.
export const ResourceStrategyMap = {
	Stock: { actions: ["read", "create", "update", "delete"] },
	// Reaching somebody: name, address, email, telephone. Everything else that
	// hangs off a contact is its own subject below, because "everyone may read
	// contacts" was only ever true of the address book — not of what a client
	// is billed, which projects it has, or which services were sold to it.
	Contact: { actions: ["read", "create", "update", "delete"] },
	// The contact persons at a client. Split from Contact because they are part
	// of reaching somebody rather than part of the commercial record.
	ContactMembers: { actions: ["read", "create", "update", "delete"] },
	// A client's projects and the services sold to it — commercial data, and the
	// reason Contact could not stay one subject.
	ContactProjects: { actions: ["read", "create", "update", "delete"] },
	ContactServices: { actions: ["read", "create", "update", "delete"] },
	Vehicle: { actions: ["read", "create", "update", "delete"] },
	Service: { actions: ["read", "create", "update", "delete"] },
	// `approve` closes a settlement with a partner organisation against further
	// booking. Same gap as on Attendance: the flow existed, the action did not.
	Partner: { actions: ["read", "create", "update", "delete", "approve"] },
	Team: { actions: ["read", "create", "update", "delete"] },
	Permission: { actions: ["manage"] },
	Absence: { actions: ["read", "create", "update", "delete", "approve"] },
	// `approve` is what a team leader does to a submitted week. Without it in the
	// manifest the approval flow in the code could not be gated at all, which is
	// how a member came to be able to approve their own hours.
	Attendance: { actions: ["read", "create", "update", "delete", "approve"] },
	// `create` is inviting somebody. Without it, the invitation routes had no
	// action-subject pair to gate on, however clearly it was decided that only
	// administrators and the owner may invite.
	User: { actions: ["read", "create", "update", "delete"] },
	// Contract data: job title, hours per week, workdays, holiday entitlement,
	// region and the periods they are valid for. Its own subject rather than a
	// corner of User, because reading your own sheet and reading a colleague's
	// are different rights and a single subject cannot tell them apart.
	Employment: { actions: ["read", "create", "update", "delete"] },
	Invoice: { actions: ["read", "create", "update", "delete"] },
	IncomingInvoice: { actions: ["read", "create", "update", "delete"] },
	// Ticket is deliberately NOT a subject. Reading tickets belongs to every member of the
	// organisation, and creating, changing and deleting one belongs to a project's members
	// (Bauleiter and deputies), the people substituting for one, and admins — a relationship to
	// the project, fixed in code, not a right an organisation configures per role
	// (`Citadel.canEditTickets`). Arranging a ticket on a board is wider by one arm the grid
	// does grant: `update Plan` OR that relationship (`Citadel.canArrangeTickets`).
	Measurement: { actions: ["read", "create", "update", "delete"] },
	Calculation: { actions: ["read", "create", "update", "delete", "approve"] },
	Project: { actions: ["read", "create", "update", "delete"] },
	Plan: { actions: ["read", "create", "update", "delete"] },
	// The organisation and its configuration objects: workspaces, alerts,
	// worksite acknowledgement definitions, table types.
	Organization: { actions: ["read", "create", "update", "delete"] },
	// File is deliberately NOT a subject. A file is a property of whatever it is attached to,
	// so attaching, replacing, unlinking, moving, annotating and removing one answer to `update`
	// on the parent — `FILE_PARENT_SUBJECTS` names which subject that is per collection, and the
	// server's files registry carries the statement that loads the parent. Reading a file is
	// every member's: the worksite risk assessments are attachments and every employee opens one
	// to clock in. A file linked to nothing — an unfiled drop, an orphan — is its uploader's and
	// the administrator's.
	Note: { actions: ["read", "create", "update", "delete"] },
	// Meeting minutes, negotiation records, general notes and the call requests a
	// receptionist logs for a colleague. Its own subject rather than a corner of
	// Project, because a protocol does not have to belong to one — a call request
	// arrives before anybody knows the job — and because writing negotiation
	// minutes is a different right from changing the project they are about.
	Protocol: { actions: ["read", "create", "update", "delete"] },
	Integration: { actions: ["read", "create", "update", "delete"] },
	// The wiki: spaces, pages, and the videos embedded in them. One subject for all three
	// because the domains reach into each other — saving a page prunes blocks whose video is
	// gone, and deleting a video rewrites the pages that embed it. Two subjects would put a
	// second gate on each of those crossings and let a role hold one half of an operation.
	Wiki: { actions: ["read", "create", "update", "delete"] },
} as const satisfies Record<string, SubjectConfig>;

// ---------------------------------------------------------------------------
// Derived types
//
// A subject exists exactly when it has a map entry, and an action is valid for
// that subject exactly when the entry lists it. Both are derived rather than
// hand-written, so adding a subject is a single edit here and an off-manifest
// pairing is a compile error — which only enforces anything because the
// typecheck gate runs on every push.
// ---------------------------------------------------------------------------

export type AppSubjectName = keyof typeof ResourceStrategyMap | "all";

// `manage` is CASL's wildcard and is always grantable; it is deliberately not
// listed per subject. "all" is the wildcard subject, so it admits any action.
export type ActionFor<S extends AppSubjectName> = S extends keyof typeof ResourceStrategyMap
	? (typeof ResourceStrategyMap)[S]["actions"][number] | "manage"
	: Action;

// Derived manifest — single source of truth. Typed explicitly so consumers
// see `actions: Action[]` rather than the per-subject literal tuples, and
// spread so the readonly source does not leak into the public shape.
export const permissionManifest: Array<{ subject: string; actions: Action[] }> = Object.entries(
	ResourceStrategyMap,
).map(([subject, { actions }]) => ({ subject, actions: [...actions] }));

export function getAllPermissionActions() {
	return uniqueArray(permissionManifest.flatMap(({ actions }) => actions));
}
